import { inject, injectable } from 'inversify';
import { Subscription as CentrifugeSubsription, RpcResult } from 'centrifuge';
import { getRealm } from 'samolet-oauth2';
import { EGlobalDIKeys } from 'core/di/keys';
import { WS_MESSAGE_TYPE } from '../../../websockets';
import { IChannel } from '../Channel/types';
import type { IWebSocketService } from '../WebSocketService';
import type { ISubscription, ISubscriptionSettings } from './types';

/**
 * Subscription - Подписка на WebSocket канал с поддержкой множественных колбэков по messageType
 *
 * Пример использования:
 *
 * const subscription = useSubscription(personalChannel);
 *
 * // Подписка на несколько типов сообщений
 * await subscription.subscribe((data) => {
 *   console.log('Export lot result:', data);
 * }, WS_MESSAGE_TYPE.EXPORT_LOT);
 *
 * await subscription.subscribe((data) => {
 *   console.log('Import proposal result:', data);
 * }, WS_MESSAGE_TYPE.IMPORT_PROPOSAL);
 *
 * // Отписка от конкретного типа
 * await subscription.unsubscribe(WS_MESSAGE_TYPE.EXPORT_LOT);
 *
 * // Полная отписка
 * await subscription.unsubscribe();
 */
@injectable()
class Subscription implements ISubscription {
  protected _Channel: IChannel;

  private _publicationHandler: ((data: any) => void) | null = null;

  private _messageTypeCallbacks: Map<WS_MESSAGE_TYPE, Set<(data: any) => any>> =
    new Map();

  private _trigger?: <T>(data?: T) => Promise<RpcResult> | undefined;

  private _subscription: CentrifugeSubsription | null = null;

  constructor(
    settings: ISubscriptionSettings,
    @inject(EGlobalDIKeys.WebSocketService)
    private readonly _webSocketService: IWebSocketService,
  ) {
    this._Channel = settings.channel;
    this._trigger = settings.trigger;
  }

  trigger<T>(data?: T) {
    return this._trigger?.(data);
  }

  subscribe = async <T = any>(
    callback: (data: T) => any,
    messageType?: WS_MESSAGE_TYPE,
    data?: any,
  ): Promise<void> => {
    const client = this._webSocketService.getClient();
    if (!client) {
      throw new Error(
        'WebSocket client not initialized. Ensure WebSocketService is initialized with token.',
      );
    }

    const channelName = this._Channel.getName();
    if (!this._subscription) {
      this._subscription = client.getSubscription(channelName);
      if (!this._subscription) {
        this._subscription = client.newSubscription(this._Channel.getName(), {
          data: {
            data: {},
            meta: {
              token: this._webSocketService?.getToken(),
              realm: getRealm(),
              ...(data || {}),
            },
          },
        });
        this._subscription.subscribe();
      }

      client.on('disconnected', this._onDisconnect);

      this._subscription.off('publication', this._handlePublication);
      this._subscription.on('publication', this._handlePublication);

      this._subscription.on('error', this._onDisconnect);
      this._subscription.on('unsubscribed', this._onDisconnect);
    }

    // Если указан messageType, добавляем колбэк для конкретного типа
    if (messageType) {
      if (!this._messageTypeCallbacks.has(messageType)) {
        this._messageTypeCallbacks.set(messageType, new Set());
      }
      this._messageTypeCallbacks.get(messageType)?.add(callback);
    }
  };

  private _onDisconnect = () => {
    this._subscription?.removeAllListeners();
    this._subscription = null;
  };

  private _handlePublication = (data: {
    channel: string;
    data: { messageType: WS_MESSAGE_TYPE; messageData: any };
  }): void => {
    const messageType = data?.data?.messageType;
    if (messageType && this._messageTypeCallbacks.has(messageType)) {
      const callbacks = this._messageTypeCallbacks.get(messageType);
      if (callbacks) {
        callbacks.forEach(callback => {
          try {
            callback(data?.data?.messageData);
          } catch (error) {
            console.error(`Error in messageType callback for ${messageType}:`, error);
          }
        });
      }
    }
  };

  unsubscribe = async (messageType?: WS_MESSAGE_TYPE): Promise<void> => {
    const client = this._webSocketService.getClient();

    // Если указан конкретный messageType, отписываемся только от него
    if (messageType) {
      this._messageTypeCallbacks.delete(messageType);

      return;
    }

    // Если messageType не указан, отписываемся от всего
    if (client && this._publicationHandler && this._subscription) {
      client.off('disconnected', this._onDisconnect);
      this._subscription.unsubscribe();
      this._subscription.removeAllListeners();
      this._publicationHandler = null;
      this._subscription = null;
    }

    // Очищаем все колбэки по типам сообщений
    this._messageTypeCallbacks.clear();
  };
}

export default Subscription;
